'use client';

import { useEffect, useState } from 'react';
import { useChatProxyHistory } from '../../../proxy-history/ChatProxyHistoryProvider';
import type { ToolConfig } from '../../tool-config-types';
import type { HttpHitlDisplayArgs } from '../../tools/http/http-hitl-payload.utils';

type HitlApprovalGateContext = {
  args: Record<string, unknown>;
  fetchHistoryRequestForHitlDisplay?: (
    requestId: string,
  ) => Promise<HttpHitlDisplayArgs | null>;
};

async function resolveHitlApprovalBlockedState({
  config,
  toolArgs,
  fetchHistoryRequestForHitlDisplay,
}: {
  config: ToolConfig;
  toolArgs?: Record<string, unknown>;
  fetchHistoryRequestForHitlDisplay?: HitlApprovalGateContext['fetchHistoryRequestForHitlDisplay'];
}): Promise<boolean> {
  if (!config.resolveHitlApprovalBlocked) {
    return false;
  }

  if (!toolArgs) {
    return true;
  }

  try {
    const result = await config.resolveHitlApprovalBlocked({
      args: toolArgs,
      fetchHistoryRequestForHitlDisplay,
    });
    return result.blocked;
  } catch (error: unknown) {
    console.warn('[useHitlApprovalGate] Failed to resolve approval gate', {
      toolName: config.name,
      error: error instanceof Error ? error : new Error(String(error)),
    });
    return true;
  }
}

export function useHitlApprovalGate({
  config,
  toolArgs,
}: {
  config: ToolConfig;
  toolArgs?: Record<string, unknown>;
}): { isApprovalBlocked: boolean } {
  const proxyHistory = useChatProxyHistory();
  const [isApprovalBlocked, setIsApprovalBlocked] = useState(
    Boolean(config.resolveHitlApprovalBlocked),
  );

  useEffect(() => {
    let cancelled = false;

    void resolveHitlApprovalBlockedState({
      config,
      toolArgs,
      fetchHistoryRequestForHitlDisplay:
        proxyHistory?.fetchHistoryRequestForHitlDisplay,
    }).then((blocked) => {
      if (!cancelled) {
        setIsApprovalBlocked(blocked);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [
    config,
    config.resolveHitlApprovalBlocked,
    config.name,
    toolArgs,
    proxyHistory?.fetchHistoryRequestForHitlDisplay,
  ]);

  return { isApprovalBlocked };
}

export function useHitlMultiActionApprovalGate({
  gatedActions,
}: {
  gatedActions: Array<{
    key: string;
    config: ToolConfig;
    toolArgs?: Record<string, unknown>;
  }>;
}): { blockedActionKeys: ReadonlySet<string> } {
  const proxyHistory = useChatProxyHistory();
  const [blockedActionKeys, setBlockedActionKeys] = useState<
    ReadonlySet<string>
  >(() => new Set());

  useEffect(() => {
    let cancelled = false;

    if (gatedActions.length === 0) {
      setBlockedActionKeys(new Set());
      return () => {
        cancelled = true;
      };
    }

    void Promise.all(
      gatedActions.map(async ({ key, config, toolArgs }) => ({
        key,
        blocked: await resolveHitlApprovalBlockedState({
          config,
          toolArgs,
          fetchHistoryRequestForHitlDisplay:
            proxyHistory?.fetchHistoryRequestForHitlDisplay,
        }),
      })),
    ).then((results) => {
      if (!cancelled) {
        setBlockedActionKeys(
          new Set(
            results
              .filter((result) => result.blocked)
              .map((result) => result.key),
          ),
        );
      }
    });

    return () => {
      cancelled = true;
    };
  }, [gatedActions, proxyHistory?.fetchHistoryRequestForHitlDisplay]);

  return { blockedActionKeys };
}
